import { useCallback, useState } from 'react';
import { Clipboard, Linking, Pressable } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';
import logger from '../../utils/logger';

// --- Styled Components ---

const Card = styled.View`
  padding: 16px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const HeaderRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 15px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const DismissButton = styled(Pressable)`
  padding: 4px;
`;

const Description = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 12px;
`;

const StepRow = styled.View`
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 10px;
  gap: 10px;
`;

const StepNumber = styled.View`
  width: 22px;
  height: 22px;
  border-radius: 11px;
  background-color: ${REDESIGN_COLORS.ctaBlue};
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

const StepNumberText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 11px;
  color: #FFFFFF;
`;

const StepText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textPrimary};
  flex: 1;
`;

const ActionRow = styled.View`
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
  margin-bottom: 12px;
`;

const ActionButton = styled(Pressable)`
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: ${(props) => props.$primary ? REDESIGN_COLORS.ctaBlue : 'rgba(255, 255, 255, 0.08)'};
  padding: 10px 14px;
  border-radius: 8px;
  border-width: ${(props) => props.$primary ? '0px' : '1px'};
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const ActionButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const ListingLink = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;

const LinkText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 12px;
  color: ${REDESIGN_COLORS.ctaBlue};
`;

const HintText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  line-height: 18px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-top: 8px;
`;

const AliasRow = styled.View`
  flex-direction: row;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  margin-top: 4px;
  margin-bottom: 12px;
`;

const AliasValue = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const AliasCopyIcon = styled(Pressable)`
  padding: 4px;
`;

const ScriptBlock = styled.View`
  margin-top: 4px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  gap: 6px;
`;

const ScriptHeaderRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ScriptLabel = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 11px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const ScriptCopyIcon = styled(Pressable)`
  padding: 4px;
`;

const ScriptBody = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 19px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

/**
 * FormBridgeCard — single canonical "how to contact this dealer" surface.
 *
 * This card is the only place the user is told how to script-handoff. It
 * absorbs what used to live across three components:
 *   - the rail-side ContactEmailCard (alias address + copy button)
 *   - the in-chat ScriptHandoffGuide (3-step pre-send / post-send copy)
 *   - the original FormBridge banner (listing-link CTA + script copy)
 *
 * Two visual states, switched by the `approvedScript` prop:
 *
 *   - pre-send  (no approved script): 3-step instructions + listing link +
 *                                     script copy + alias email block.
 *   - post-send (approved script exists, dealer hasn't replied yet): waiting
 *                                     callout + the script the user sent +
 *                                     alias email so they can fix the
 *                                     reply-to mid-flow if needed.
 *
 * @param {Object} props
 * @param {string} [props.coachingText]   - The latest unsent draft body.
 * @param {string} [props.approvedScript] - Body of the last approved draft;
 *                                          when present, switches to the
 *                                          post-send waiting state.
 * @param {string} [props.aliasEmail]     - Per-negotiation alias address.
 * @param {string} [props.listingUrl]     - URL of the dealer's listing page.
 * @param {Function} [props.onDismiss]    - Called when user dismisses the card.
 */
const FormBridgeCard = ({
  coachingText,
  approvedScript,
  aliasEmail,
  listingUrl,
  onDismiss,
}) => {
  const [copiedScript, setCopiedScript] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const copyToClipboard = useCallback(async (text, setCopiedFn) => {
    if (!text) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        Clipboard.setString(text);
      }
      setCopiedFn(true);
      setTimeout(() => setCopiedFn(false), 2000);
    } catch (err) {
      logger.warn('[FormBridgeCard] Clipboard write failed:', err);
    }
  }, []);

  const handleOpenListing = useCallback(() => {
    if (listingUrl) {
      Linking.openURL(listingUrl);
    }
  }, [listingUrl]);

  const isPostSend = !!approvedScript;
  const scriptForCopy = approvedScript || coachingText || '';

  const aliasBlock = aliasEmail ? (
    <AliasRow>
      <AliasValue selectable numberOfLines={1}>{aliasEmail}</AliasValue>
      <AliasCopyIcon
        onPress={() => copyToClipboard(aliasEmail, setCopiedEmail)}
        accessibilityLabel="Copy reply-to email"
        accessibilityRole="button"
        hitSlop={8}
      >
        <Icon
          name={copiedEmail ? 'check' : 'content-copy'}
          size={14}
          color={REDESIGN_COLORS.textSecondary}
        />
      </AliasCopyIcon>
    </AliasRow>
  ) : null;

  return (
    <Card>
      <HeaderRow>
        <Title>
          {isPostSend
            ? "Waiting for the dealer's reply"
            : 'How to contact this dealer'}
        </Title>
        {onDismiss && (
          <DismissButton onPress={onDismiss} hitSlop={8}>
            <Icon name="close" size={18} color={REDESIGN_COLORS.textSecondary} />
          </DismissButton>
        )}
      </HeaderRow>

      {isPostSend ? (
        <>
          <Description>
            We'll listen for any reply and draft your follow-up automatically.
            Keep this page open in case you need to copy the script or your
            reply-to address again.
          </Description>

          {aliasBlock}

          {scriptForCopy ? (
            <ScriptBlock>
              <ScriptHeaderRow>
                <ScriptLabel>The script you sent</ScriptLabel>
                <ScriptCopyIcon
                  onPress={() => copyToClipboard(scriptForCopy, setCopiedScript)}
                  accessibilityLabel="Copy the script"
                  accessibilityRole="button"
                  hitSlop={8}
                >
                  <Icon
                    name={copiedScript ? 'check' : 'content-copy'}
                    size={14}
                    color={REDESIGN_COLORS.textSecondary}
                  />
                </ScriptCopyIcon>
              </ScriptHeaderRow>
              <ScriptBody selectable>{scriptForCopy}</ScriptBody>
            </ScriptBlock>
          ) : null}

          <HintText>
            If the dealer replies by phone or in person, tap "I have a message
            from the dealer" at the bottom of the chat so we can pick it up.
          </HintText>
        </>
      ) : (
        <>
          <Description>
            This dealer doesn't have a direct email on file. Follow these
            steps to reach them. Once they reply by email, we'll take over
            and handle the rest automatically.
          </Description>

          <StepRow>
            <StepNumber><StepNumberText>1</StepNumberText></StepNumber>
            <StepText>Go to the dealer's listing and find their contact or enquiry form.</StepText>
          </StepRow>

          {listingUrl && (
            <ListingLink onPress={handleOpenListing} style={{ marginLeft: 32, marginBottom: 10 }}>
              <Icon name="open-in-new" size={14} color={REDESIGN_COLORS.ctaBlue} />
              <LinkText>Open listing on dealer site</LinkText>
            </ListingLink>
          )}

          <StepRow>
            <StepNumber><StepNumberText>2</StepNumberText></StepNumber>
            <StepText>Paste the script below as your message, and use the reply-to address shown so any reply lands here.</StepText>
          </StepRow>

          {scriptForCopy ? (
            <ActionRow>
              <ActionButton
                onPress={() => copyToClipboard(scriptForCopy, setCopiedScript)}
              >
                <Icon name="script-text-outline" size={14} color={REDESIGN_COLORS.textPrimary} />
                <ActionButtonText>{copiedScript ? 'Copied!' : 'Copy script'}</ActionButtonText>
              </ActionButton>
            </ActionRow>
          ) : null}

          {aliasBlock}

          <StepRow>
            <StepNumber><StepNumberText>3</StepNumberText></StepNumber>
            <StepText>Submit the form and wait for their reply. It will appear in this chat and we'll draft your next response.</StepText>
          </StepRow>

          <HintText>
            You can also call or visit the dealer in person. Use the script as
            a guide, then report what they said so we can pick up the
            negotiation.
          </HintText>
        </>
      )}
    </Card>
  );
};

export default FormBridgeCard;
